import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import React, { useCallback, useEffect, useState } from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { db } from "../../firebase";
import DeleteButtonFile from "../Buttons/DeleteButtonFile";
import { showCustomToast } from "../Misc/CustomToast";
import "./UploaderAraEncapsulated.css";

const inputId = "treatmentPlanUploadFiles";
const maxFileSize = 100 * 1024 * 1024;
const acceptedFiles =
  "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z";

function UploaderTreatmentPlanModern({ clientId }) {
  const [fileLinks, setFileLinks] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchFileUrls = useCallback(async () => {
    if (!clientId) return;
    const snapshot = await getDoc(doc(db, "clients", clientId));
    if (snapshot.exists()) {
      setFileLinks(snapshot.data().fileUrlsTreatmentPlans || []);
    }
  }, [clientId]);

  useEffect(() => {
    fetchFileUrls();
  }, [fetchFileUrls]);

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 3) {
      showCustomToast("You can only upload up to 3 files.", "error");
      return;
    }
    if (!selectedFiles.length) return;

    const validFiles = selectedFiles.filter((file) => {
      if (file.size > maxFileSize) {
        showCustomToast(`File size exceeds the limit: ${file.name}`, "error");
        return false;
      }
      return true;
    });
    if (!validFiles.length) return;

    setIsLoading(true);
    setUploadingFiles(
      validFiles.map((file) => ({
        name: file.name,
        progress: 0,
        status: "uploading",
      }))
    );

    const uploads = validFiles.map(
      (file) =>
        new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(
            ref(
              getStorage(),
              `Documents/ScannedTreatmentPlans/${clientId}/${file.name}`
            ),
            file
          );

          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadingFiles((current) =>
                current.map((item) =>
                  item.name === file.name
                    ? {
                        ...item,
                        progress,
                        status: progress >= 100 ? "complete" : "uploading",
                      }
                    : item
                )
              );
            },
            (error) => {
              setUploadingFiles((current) =>
                current.map((item) =>
                  item.name === file.name ? { ...item, status: "error" } : item
                )
              );
              reject(error);
            },
            async () => {
              try {
                resolve({
                  name: file.name,
                  url: await getDownloadURL(uploadTask.snapshot.ref),
                });
              } catch (error) {
                reject(error);
              }
            }
          );
        })
    );

    try {
      const fileUrls = await Promise.all(uploads);
      const clientRef = doc(db, "clients", clientId);
      const snapshot = await getDoc(clientRef);
      const existingUrls = snapshot.exists()
        ? snapshot.data().fileUrlsTreatmentPlans || []
        : [];
      await updateDoc(clientRef, {
        fileUrlsTreatmentPlans: [...existingUrls, ...fileUrls],
      });
      await fetchFileUrls();
      setUploadingFiles([]);
      const input = document.getElementById(inputId);
      if (input) input.value = "";
    } catch (error) {
      console.error("Error uploading treatment plan files: ", error);
      showCustomToast("Error uploading files", "error");
      setUploadingFiles((current) =>
        current.map((file) => ({ ...file, status: "error" }))
      );
    } finally {
      setIsLoading(false);
    }
  };

  const deleteFile = async (fileUrl, fileName) => {
    try {
      await deleteObject(
        ref(
          getStorage(),
          `Documents/ScannedTreatmentPlans/${clientId}/${fileName}`
        )
      );
      const newFileLinks = fileLinks.filter((file) => file.url !== fileUrl);
      await updateDoc(doc(db, "clients", clientId), {
        fileUrlsTreatmentPlans: newFileLinks,
      });
      setFileLinks(newFileLinks);
      showCustomToast("File successfully deleted!", "success");
    } catch (error) {
      console.error("Error deleting treatment plan file: ", error);
      showCustomToast("Error deleting file", "error");
    }
  };

  return (
    <div className="ara-uploader treatment-plan-uploader">
      <section className="ara-upload-card">
        <div className="ara-upload-heading">
          <div className="ara-upload-icon">
            <CloudUploadOutlinedIcon />
          </div>
          <div>
            <span className="ara-kicker">Secure document library</span>
            <h3>Upload treatment plan documents</h3>
            <p>Add up to 3 files. Uploads start automatically after selection.</p>
          </div>
        </div>
        <label className="ara-dropzone" htmlFor={inputId}>
          <input
            type="file"
            id={inputId}
            multiple
            onChange={handleFileChange}
            accept={acceptedFiles}
          />
          <CloudUploadOutlinedIcon className="ara-dropzone-icon" />
          <strong>Drop files here or browse</strong>
          <span>Images, PDF and office files · 100 MB maximum per file</span>
          <em>Select files</em>
        </label>
      </section>

      {uploadingFiles.length > 0 && (
        <section className="ara-files-card">
          <div className="ara-section-heading">
            <div>
              <span className="ara-kicker">
                {isLoading ? "Live upload" : "Latest upload"}
              </span>
              <h3>Upload progress</h3>
            </div>
            <span className="ara-count">
              {uploadingFiles.filter((file) => file.status === "complete").length}/
              {uploadingFiles.length}
            </span>
          </div>
          <div className="ara-progress-list">
            {uploadingFiles.map((file, index) => (
              <div className="ara-progress-item" key={`${file.name}-${index}`}>
                <div className="ara-file-icon">
                  <DescriptionOutlinedIcon />
                </div>
                <div className="ara-file-details">
                  <strong title={file.name}>{file.name}</strong>
                  <span>
                    {file.status === "complete"
                      ? "Uploaded"
                      : file.status === "error"
                        ? "Upload failed"
                        : `Uploading ${Math.round(file.progress)}%`}
                  </span>
                  <div className="ara-progress-track">
                    <i style={{ width: `${file.progress}%` }} />
                  </div>
                </div>
                {file.status === "complete" ? (
                  <CheckCircleOutlineIcon className="ara-success" />
                ) : file.status === "error" ? (
                  <ErrorOutlineIcon className="ara-error" />
                ) : (
                  <b>{Math.round(file.progress)}%</b>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="ara-files-card">
        <div className="ara-section-heading">
          <div>
            <span className="ara-kicker">Client library</span>
            <h3>Uploaded documents</h3>
          </div>
          <span className="ara-count">{fileLinks.length}</span>
        </div>
        <div className="ara-existing-list">
          {fileLinks.length ? (
            fileLinks.map((file, index) => (
              <div key={`${file.url}-${index}`} className="ara-existing-file">
                <div className="ara-file-icon">
                  <DescriptionOutlinedIcon />
                </div>
                <a href={file.url} target="_blank" rel="noreferrer" title={file.name}>
                  {file.name}
                </a>
                <DeleteButtonFile
                  fileName={file.name}
                  onClick={() => deleteFile(file.url, file.name)}
                />
              </div>
            ))
          ) : (
            <div className="ara-empty">No documents uploaded yet.</div>
          )}
        </div>
      </section>
    </div>
  );
}

export default UploaderTreatmentPlanModern;
